import { orderLayoutBoxes } from '../../utils/privateLayouts';
import React, { useRef, useEffect, useState } from 'react';
import { Stage, Layer, Image as KonvaImage } from 'react-konva';
import axios from 'axios';
import { saveCanvasImage } from '../../features/edit-page/api/pageApi';
import PropTypes from 'prop-types';
import PageTextItemNode from '../../features/edit-page/components/PageTextItemNode';
import { BUILT_IN_NUMBER_ASSETS, normalizeSvgForStroke } from '../../pages/VectorLayoutEditor';
import { waitForCanvasFonts } from '../../utils/presetLayout';

// Rendering is read-only until the finished image is saved. The same node as
// the editor renders every present box; no preset/template may invent a box.
export default function BackgroundCanvasProcessorPresets({ pageId, selectedLanguage, onDone }) {
  const stageRef = useRef(null);
  const reported = useRef(false);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  const [render, setRender] = useState(null);
  useEffect(() => {
    let cancelled = false;
    reported.current = false;
    setRender(null);
    const done = (result) => {
      if (cancelled || reported.current) return;
      reported.current = true;
      onDoneRef.current?.({ ...result, pageId });
    };
    const load = async () => {
      const response = await axios.get(`/api/page/${pageId}?plang=${selectedLanguage}`);
      const page = response.data;
      if (!(page.width > 0 && page.height > 0)) throw new Error('This page has no valid canvas dimensions.');
      if (page.layout_version !== 1) throw new Error('This legacy page needs its layout migrated or explicitly saved in the page editor before rendering.');
      const [stylesResponse, bookResponse] = await Promise.all([
        axios.get('/api/design-presets'), axios.get(`/api/book/${page.book_id}`),
      ]);
      await waitForCanvasFonts();
      const image = await new Promise((resolve, reject) => {
        const img = new window.Image();
        img.crossOrigin = 'Anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Could not load the original page artwork.'));
        img.src = `/uploads/${page.image_path}`;
      });
      const languages = selectedLanguage.split('_').filter(Boolean);
      const entries = new Map((page.page_text_entries || []).map((entry) => [entry.id || entry.entry_id, entry]));
      const mainTexts = Object.fromEntries(languages.map((lang) => [lang, page[`text_${lang}`] || '']));
      const items = (page.text_items || []).filter((item) => item.visibleOnCanvas !== false)
        .map((item) => ({ ...item, texts: item.role === 'main' || item.sourceType === 'page_main'
          ? mainTexts : entries.get(item.sourceTextEntryId)?.texts || {} }));
      const decorations = [];
      if (page.vector_layout_id) {
        const layouts = (await axios.get(`/api/vector-layouts?book_id=${page.book_id}`)).data.vector_layouts || [];
        const layout = layouts.find((candidate) => candidate._id === page.vector_layout_id);
        if (!layout) throw new Error('A saved decoration is missing. Review this page before rendering.');
        const assets = [...BUILT_IN_NUMBER_ASSETS, ...(layout.assets || [])];
        for (const item of layout.items || []) {
          const asset = assets.find((candidate) => candidate.id === item.assetId);
          if (!asset) throw new Error('A saved decoration asset is missing.');
          const svg = normalizeSvgForStroke(asset.svgText, item.strokeWidth || 4, item.strokeColor || '#000000');
          const decoration = await new Promise((resolve, reject) => {
            const img = new window.Image(); img.onload = () => resolve(img);
            img.onerror = () => reject(new Error('A saved decoration could not be loaded.'));
            img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
          });
          const sx = page.width / (layout.page_width || page.width);
          const sy = page.height / (layout.page_height || page.height);
          decorations.push({ image: decoration, x: (Number(item.x) || 0) * sx, y: (Number(item.y) || 0) * sy,
            width: (Number(item.width) || 100) * sx, height: (Number(item.height) || 100) * sy, rotation: Number(item.rotation) || 0 });
        }
      }
      if (!cancelled) setRender({ page, image, items: orderLayoutBoxes(items, page.page_text_entries), languages, decorations, presets: stylesResponse.data, defaultId: bookResponse.data.default_preset_id });
    };
    load().catch((error) => done({ error: error.message }));
    return () => { cancelled = true; };
  }, [pageId, selectedLanguage]);

  useEffect(() => {
    if (!render) return undefined;
    let cancelled = false;
    // Let React commit all Konva nodes, including a valid empty layout.
    const timer = setTimeout(async () => {
      try {
        if (!stageRef.current) throw new Error('The rendering canvas is unavailable.');
        stageRef.current.draw();
        const blob = await stageRef.current.toBlob({ pixelRatio: 1, mimeType: 'image/png' });
        if (!blob) throw new Error('The page image could not be rendered.');
        if (cancelled) return;
        const data = new FormData();
        data.append('image', blob, `page_${selectedLanguage}_${pageId}.png`);
        data.append('page_id', pageId); data.append('language', selectedLanguage);
        data.append('book_id', render.page.book_id);
        await saveCanvasImage(data);
        if (!cancelled && !reported.current) { reported.current = true; onDoneRef.current?.({ success: true, pageId }); }
      } catch (error) {
        if (!cancelled && !reported.current) { reported.current = true; onDoneRef.current?.({ error: error.message, pageId }); }
      }
    }, 100);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [render, pageId, selectedLanguage]);

  if (!render) return null;
  const { page, image, items, languages, decorations, presets, defaultId } = render;
  return <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
    <Stage width={page.width} height={page.height} ref={stageRef}><Layer>
      <KonvaImage image={image} width={page.width} height={page.height} />
      {items.map((item) => <PageTextItemNode key={item.id} item={item} languages={languages}
        presets={presets} bookDefaultPresetId={defaultId} canvasSize={{ width: page.width, height: page.height }} />)}
      {decorations.map((decoration, index) => <KonvaImage key={`decoration-${index}`} {...decoration} listening={false} />)}
    </Layer></Stage>
  </div>;
}
BackgroundCanvasProcessorPresets.propTypes = {
  pageId: PropTypes.string.isRequired, selectedLanguage: PropTypes.string.isRequired, onDone: PropTypes.func,
};
